import * as ElasticAppSearch from '@elastic/app-search-javascript';

import { ArticleSearchObject } from '../../../../../services/search/config/articles/types';
import { ProductSearchObject } from '../../../../../services/search/config/products/types';
import { ProviderSearchResults } from '../types';

type SearchRawObject<T> = {
  [P in keyof T]: {
    raw: T[P];
  };
};

type ArticleSearchRawObject = SearchRawObject<ArticleSearchObject>;
type ProductSearchRawObject = SearchRawObject<ProductSearchObject>;

const isArticleSearchRawObject = (
  searchRawObject: ProductSearchRawObject | ArticleSearchRawObject
): searchRawObject is ArticleSearchRawObject =>
  searchRawObject.type.raw === 'article';

const isProductSearchRawObject = (
  searchRawObject: ProductSearchRawObject | ArticleSearchRawObject
): searchRawObject is ProductSearchRawObject =>
  searchRawObject.type.raw === 'product';

export const executeSearch = async (
  searchQuery: string,
  lang: string
): Promise<ProviderSearchResults> => {
  const elasticsearchClient = ElasticAppSearch.createClient({
    searchKey: process.env.GATSBY_ELASTICSEARCH_SEARCH_KEY,
    endpointBase: process.env.GATSBY_ELASTICSEARCH_URL,
    engineName: process.env.GATSBY_ELASTICSEARCH_ENGINE,
  });

  const filter = {
    filters: {
      lang: [lang],
    },
  };

  const searchResult = await elasticsearchClient.search(searchQuery, filter);

  const articles: Array<ArticleSearchObject> = [];
  const products: Array<ProductSearchObject> = [];

  searchResult?.results.forEach(
    ({ data }: { data: ArticleSearchRawObject | ProductSearchRawObject }) => {
      if (isArticleSearchRawObject(data)) {
        const result: ArticleSearchObject = {
          id: data.id.raw,

          url: data.url.raw,
          title: data.title.raw,
          description: data.description.raw,

          seo_description: data.seo_description.raw,
          seo_keywords: data.seo_keywords.raw,
          seo_title: data.seo_title.raw,

          lang: data.lang.raw,
          type: 'article',
        };
        articles.push(result);
      }

      if (isProductSearchRawObject(data)) {
        const result: ProductSearchObject = {
          id: data.id.raw,

          url: data.url.raw,
          title: data.title.raw,
          description: data.description.raw,

          sku: data.sku.raw,
          image_title: data.image_title.raw,
          image_url: data.image_url.raw,
          image_data: data.image_data.raw,

          seo_description: data.seo_description.raw,
          seo_keywords: data.seo_keywords.raw,
          seo_title: data.seo_title.raw,

          aroma_scent: data.aroma_scent.raw,
          net_content_statement: data.net_content_statement.raw,
          brand_name: data.brand_name.raw,
          barcode_number: data.barcode_number.raw,
          segment_description: data.segment_description.raw,

          lang: data.lang.raw,
          type: 'product',
        };
        products.push(result);
      }
    }
  );

  return {
    articles,
    products,
  };
};
